"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getPortalLanguageOption,
  isPortalLanguageCode,
  portalLanguageOptions,
  translatePortalText,
  type PortalLanguageCode,
} from "@/shared/lib/portalTranslations";

const portalLanguageStorageKey = "carevinco-requester-language";
const translatedAttributes = ["aria-label", "placeholder", "title"] as const;
const ignoredElementNames = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "IFRAME"]);
const portalTranslationScopeSelector = [
  ".elderly-portal",
  ".MuiModal-root",
  ".MuiPopover-root",
  ".MuiMenu-root",
  ".MuiPopper-root",
  ".MuiSnackbar-root",
  ".care-global-progress",
  ".app-startup-screen",
  ".domain-route-error",
].join(", ");
const useBrowserLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

type TranslationRecord = {
  lastApplied: string;
  source: string;
};

type PortalLanguageContextValue = {
  language: PortalLanguageCode;
  languageOption: (typeof portalLanguageOptions)[number];
  setLanguage: (language: PortalLanguageCode) => void;
  t: (value: string) => string;
};

const PortalLanguageContext = createContext<PortalLanguageContextValue | null>(null);
const textRecords = new WeakMap<Text, TranslationRecord>();
const attributeRecords = new WeakMap<Element, Map<string, TranslationRecord>>();

function getNodeElement(node: Node) {
  if (node.nodeType === Node.ELEMENT_NODE) {
    return node as Element;
  }

  return node.parentElement;
}

function isInPortalTranslationScope(node: Node) {
  const element = getNodeElement(node);

  return Boolean(
    element?.matches(portalTranslationScopeSelector) ||
    element?.closest(portalTranslationScopeSelector),
  );
}

function containsPortalTranslationScope(node: Node) {
  if (
    node.nodeType !== Node.ELEMENT_NODE &&
    node.nodeType !== Node.DOCUMENT_NODE &&
    node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE
  ) {
    return false;
  }

  return Boolean(
    (node as Element | Document | DocumentFragment).querySelector(
      portalTranslationScopeSelector,
    ),
  );
}

function shouldIgnoreNode(node: Node) {
  const element = getNodeElement(node);

  return Boolean(
    element?.closest(
      "[data-portal-translation-ignore], script, style, noscript, iframe",
    ),
  );
}

function translateTextNode(node: Text, language: PortalLanguageCode) {
  if (!isInPortalTranslationScope(node) || shouldIgnoreNode(node)) {
    return;
  }

  const current = node.nodeValue ?? "";

  if (!current.trim()) {
    return;
  }

  const existing = textRecords.get(node);
  const source =
    existing && current !== existing.lastApplied && current !== existing.source
      ? current
      : existing?.source ?? current;
  const translated = translatePortalText(source, language);

  textRecords.set(node, { lastApplied: translated, source });

  if (current !== translated) {
    node.nodeValue = translated;
  }
}

function translateElementAttributes(element: Element, language: PortalLanguageCode) {
  if (
    !isInPortalTranslationScope(element) ||
    ignoredElementNames.has(element.tagName) ||
    element.closest("[data-portal-translation-ignore]")
  ) {
    return;
  }

  let elementRecords = attributeRecords.get(element);

  if (!elementRecords) {
    elementRecords = new Map();
    attributeRecords.set(element, elementRecords);
  }

  for (const attribute of translatedAttributes) {
    const current = element.getAttribute(attribute);

    if (!current?.trim()) {
      continue;
    }

    const existing = elementRecords.get(attribute);
    const source =
      existing && current !== existing.lastApplied && current !== existing.source
        ? current
        : existing?.source ?? current;
    const translated = translatePortalText(source, language);

    elementRecords.set(attribute, { lastApplied: translated, source });

    if (current !== translated) {
      element.setAttribute(attribute, translated);
    }
  }
}

function translateSubtree(root: Node, language: PortalLanguageCode) {
  if (root.nodeType === Node.TEXT_NODE) {
    translateTextNode(root as Text, language);
    return;
  }

  if (
    root.nodeType !== Node.ELEMENT_NODE &&
    root.nodeType !== Node.DOCUMENT_NODE &&
    root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE
  ) {
    return;
  }

  if (!isInPortalTranslationScope(root) && !containsPortalTranslationScope(root)) {
    return;
  }

  if (root.nodeType === Node.ELEMENT_NODE) {
    translateElementAttributes(root as Element, language);
  }

  const textWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let textNode = textWalker.nextNode();

  while (textNode) {
    translateTextNode(textNode as Text, language);
    textNode = textWalker.nextNode();
  }

  const elementWalker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  let elementNode = elementWalker.nextNode();

  while (elementNode) {
    translateElementAttributes(elementNode as Element, language);
    elementNode = elementWalker.nextNode();
  }
}

export function PortalLanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<PortalLanguageCode>("en");

  const setLanguage = useCallback((nextLanguage: PortalLanguageCode) => {
    document.documentElement.lang = nextLanguage;
    setLanguageState(nextLanguage);

    try {
      window.localStorage.setItem(portalLanguageStorageKey, nextLanguage);
    } catch {
      // A blocked storage API must not prevent an in-memory language change.
    }
  }, []);

  useBrowserLayoutEffect(() => {
    try {
      const storedLanguage = window.localStorage.getItem(portalLanguageStorageKey);
      if (isPortalLanguageCode(storedLanguage)) {
        document.documentElement.lang = storedLanguage;
        setLanguageState(storedLanguage);
      }
    } catch {
      // English remains the default when storage is unavailable.
    }
  }, []);

  useBrowserLayoutEffect(() => {
    document.documentElement.lang = language;
    document
      .querySelectorAll(portalTranslationScopeSelector)
      .forEach((root) => translateSubtree(root, language));

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === "characterData") {
          translateTextNode(record.target as Text, language);
          continue;
        }

        if (record.type === "attributes") {
          translateElementAttributes(record.target as Element, language);
          continue;
        }

        for (const addedNode of record.addedNodes) {
          translateSubtree(addedNode, language);
        }
      }
    });

    observer.observe(document.body, {
      attributeFilter: [...translatedAttributes],
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [language]);

  useBrowserLayoutEffect(
    () => () => {
      document
        .querySelectorAll(portalTranslationScopeSelector)
        .forEach((root) => translateSubtree(root, "en"));
      document.documentElement.lang = "en";
    },
    [],
  );

  useEffect(() => {
    function syncLanguage(event: StorageEvent) {
      if (event.key !== portalLanguageStorageKey) {
        return;
      }

      const nextLanguage = isPortalLanguageCode(event.newValue)
        ? event.newValue
        : "en";
      document.documentElement.lang = nextLanguage;
      setLanguageState(nextLanguage);
    }

    window.addEventListener("storage", syncLanguage);
    return () => window.removeEventListener("storage", syncLanguage);
  }, []);

  const value = useMemo<PortalLanguageContextValue>(
    () => ({
      language,
      languageOption: getPortalLanguageOption(language),
      setLanguage,
      t: (text) => translatePortalText(text, language),
    }),
    [language, setLanguage],
  );

  return (
    <PortalLanguageContext.Provider value={value}>
      {children}
    </PortalLanguageContext.Provider>
  );
}

export function usePortalLanguage() {
  const context = useContext(PortalLanguageContext);

  if (!context) {
    throw new Error("usePortalLanguage must be used within PortalLanguageProvider");
  }

  return context;
}
